import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentTimezoneConfig } from '../../utils/timezoneUtils';
import { useTenant } from '../../contexts/TenantContext';

export default function TimezoneNotificationBar() {
  const navigate = useNavigate();
  const { currentSchool, currentOrganization } = useTenant();
  
  // Get the configured user timezone from settings
  const userConfiguredTimezone = getCurrentTimezoneConfig();
  
  // Get the school's timezone (backend timezone)
  const schoolTimezone = currentSchool?.timezone || 'America/New_York'; // Default to EST if no school timezone
  
  // Helper function to get a user-friendly timezone name
  const getTimezoneName = (timezone) => {
    if (!timezone || timezone === 'LOCAL') {
      // If LOCAL, use device timezone
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return deviceTimezone.replace(/_/g, ' ');
    }
    
    // For known abbreviations, convert to full names
    const timezoneMap = {
      'UTC': 'UTC',
      'EST': 'America/New_York',
      'PST': 'America/Los_Angeles',
      'CST': 'America/Chicago',
      'MST': 'America/Denver'
    };
    
    const resolvedTimezone = timezoneMap[timezone] || timezone;
    return resolvedTimezone.replace(/_/g, ' ');
  };
  
  // Helper function to get timezone abbreviation for display
  const getTimezoneAbbreviation = (timezone) => {
    if (!timezone || timezone === 'LOCAL') {
      // Get device timezone abbreviation
      const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const date = new Date();
      try {
        // Use Intl.DateTimeFormat to get proper timezone abbreviation
        const formatter = new Intl.DateTimeFormat('en', {
          timeZone: deviceTimezone,
          timeZoneName: 'short'
        });
        const parts = formatter.formatToParts(date);
        const timeZonePart = parts.find(part => part.type === 'timeZoneName');
        return timeZonePart ? timeZonePart.value : 'Local';
      } catch (error) {
        return 'Local';
      }
    }
    
    // For configured timezone, get proper abbreviation
    const date = new Date();
    try {
      const resolvedTimezone = timezone === 'UTC' ? 'UTC' : timezone;
      
      // Use Intl.DateTimeFormat to get proper timezone abbreviation
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: resolvedTimezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(date);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      
      if (timeZonePart && timeZonePart.value && !timeZonePart.value.match(/^(AM|PM)$/)) {
        return timeZonePart.value;
      }
      
      // Fallback to manual mapping for common timezones
      const timezoneAbbreviations = {
        'America/New_York': 'EST',
        'America/Los_Angeles': 'PST', 
        'America/Chicago': 'CST',
        'America/Denver': 'MST',
        'Africa/Nairobi': 'EAT',
        'Europe/London': 'GMT',
        'Europe/Paris': 'CET',
        'Asia/Tokyo': 'JST',
        'UTC': 'UTC'
      };
      
      return timezoneAbbreviations[resolvedTimezone] || resolvedTimezone.split('/').pop();
    } catch (error) {
      return timezone;
    }
  };
  
  const schoolTimezoneName = getTimezoneName(schoolTimezone);
  const userTimezoneName = getTimezoneName(userConfiguredTimezone);
  
  const schoolTzAbbr = getTimezoneAbbreviation(schoolTimezone);
  const userTzAbbr = getTimezoneAbbreviation(userConfiguredTimezone);
  
  // Only show the notification if user timezone is configured and different from school timezone
  const showNotification = userConfiguredTimezone && 
    userConfiguredTimezone !== 'LOCAL' && 
    getTimezoneName(userConfiguredTimezone) !== getTimezoneName(schoolTimezone);
  
  const handleAccountSettingsClick = () => {
    const orgId = currentOrganization?.unique_id;
    if (orgId) {
      navigate(`/org/${orgId}/account-settings`);
    }
  };
  
  if (!showNotification) {
    return null;
  }
  
  return (
    <div className="bg-blue-50 border-b border-blue-200">
      <div className="px-4 py-1.5">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-800">
            <span className="font-medium">Timezone Notice:</span>
            {' '}All times are being converted from school time <span className="font-semibold">{schoolTimezoneName} ({schoolTzAbbr})</span>
            {' '}to your configured time <span className="font-semibold">{userTimezoneName} ({userTzAbbr})</span> and vice versa.
            {' '}
            <span 
              className="text-blue-600 hover:text-blue-700 cursor-pointer underline"
              onClick={handleAccountSettingsClick}
            >
              Change in Account Settings →
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
